import React from 'react';
import { StyleSheet, View } from 'react-native';
import { format, parseISO } from 'date-fns';
import { BodyText, Button, noop, theme, VStack } from '@sg/garnish';

import { type SubscriptionCreditCard } from '@order/graphql';
import { type useSweetpassPlusPlans } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';

import { PaymentBlockCardIcons } from '../PaymentBlockCardIcons';
import { SweetpassCheckoutCardPicker } from '../SweetpassCheckoutCardPicker';
import { SweetpassCheckoutCheckbox } from '../SweetpassCheckoutCheckbox';
import { SweetpassMembershipPlanSelector } from '../SweetpassMembershipPlanSelector';

// ─────────────────────────────────────────────────────────────────────────────

export const SweetpassSwitchSubscriptionForm = (
  props: SweetpassSwitchSubscriptionFormProps,
) => {
  const {
    currentPlanId = '',
    nextBillingDate = '',
    plans,
    creditCards,
    selectedCreditCard,
    hasAcceptedTerms,
    canSwitchSubscription,
    isSwitchingSubscription,
    termsAndConditions,
    handleAcceptTerms,
    handleAddCreditCard,
    handleSelectCreditCard,
    handleSwitchSubscription,
  } = props;

  const hasValidPlans =
    plans?.monthly !== undefined && plans?.annual !== undefined;

  const selectedPlanId =
    currentPlanId === plans?.annual?.id
      ? plans?.monthly?.id
      : plans?.annual?.id;

  const selectedPlanPrice = Number(
    currentPlanId === plans?.annual?.id
      ? plans?.monthly?.price
      : plans?.annual?.price,
  );

  // ─── Localization ────────────────────────────────────────────────────

  const { t, formatPrice } = useLocalizationContext();

  const formattedSwitchDate = format(
    parseISO(nextBillingDate),
    'MMMM dd, yyyy',
  );

  const price = selectedPlanPrice
    ? formatPrice(selectedPlanPrice * 100, 'USD')
    : '';

  const billingFrequency =
    currentPlanId === plans?.annual?.id
      ? t('account.sweetpass-membership.membership-level.month')
      : t('account.sweetpass-membership.membership-level.year');

  const planSwitchTerms = termsAndConditions
    .replaceAll('{nextBillingDate}', formattedSwitchDate)
    .replaceAll('{price}', price)
    .replaceAll('{billingFrequency}', billingFrequency);

  const planSwitchNotice = t('sweetpass.change-subscription.change-date', {
    nextBillingDate: formattedSwitchDate,
  });

  const cta = t('sweetpass.change-subscription.confirm');
  const ctaA11y = t('sweetpass.change-subscription.confirm.a11y');

  return (
    <VStack style={styles.modalContent} gap={theme.spacing['4']}>
      {hasValidPlans ? (
        <View style={styles.membershipPlanSelectorContainer}>
          <SweetpassMembershipPlanSelector
            selectedPlanId={selectedPlanId}
            plans={plans}
            onSelectedPlanChange={noop}
            shouldHighlightSelectedPlan
            shouldDisableOtherPlanControl
          />
        </View>
      ) : null}

      <View>
        <PaymentBlockCardIcons />

        <SweetpassCheckoutCardPicker
          selectedCreditCard={selectedCreditCard}
          creditCards={creditCards}
          handleAddCreditCard={handleAddCreditCard}
          handleSelectCreditCard={handleSelectCreditCard}
        />
      </View>

      <BodyText sizeMatch={['16']} bold>
        {planSwitchNotice}
      </BodyText>

      <SweetpassCheckoutCheckbox
        hasAcceptedTerms={hasAcceptedTerms}
        termsAndConditions={planSwitchTerms}
        handleAcceptTerms={handleAcceptTerms}
      />

      <Button
        size="large"
        accessibilityRole="button"
        accessibilityLabel={ctaA11y}
        disabled={!canSwitchSubscription}
        isLoading={isSwitchingSubscription}
        onPress={handleSwitchSubscription}
      >
        {cta}
      </Button>
    </VStack>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  modalContent: {
    padding: theme.spacing['6'],
  },
  membershipPlanSelectorContainer: {
    marginBottom: theme.spacing['10'],
  },
});

// ─── Types ─────────────────────────────────────────────────────────────

type SweetpassSwitchSubscriptionFormProps = Readonly<{
  currentPlanId?: string;
  nextBillingDate?: string;
  plans: ReturnType<typeof useSweetpassPlusPlans>['plans'];
  creditCards: ReadonlyArray<Partial<SubscriptionCreditCard>>;
  selectedCreditCard?: Partial<SubscriptionCreditCard>;
  hasAcceptedTerms: boolean;
  canSwitchSubscription: boolean;
  isSwitchingSubscription: boolean;
  termsAndConditions: string;
  handleAcceptTerms: (hasAcceptedTerms: boolean) => void;
  handleAddCreditCard: () => void;
  handleSelectCreditCard: (creditCard: Partial<SubscriptionCreditCard>) => void;
  handleSwitchSubscription: () => void;
}>;
